import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import { motion, AnimatePresence } from "framer-motion";

import browseFabIcon from "@/assets/browse-fab-icon.png";
import { ChevronRight, ChevronUp, Compass, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage, type SiteLang } from "@/hooks/useLanguage";
import { QA_OVERVIEW_CARDS } from "@/data/qaOverview";
import { isRemovedQaNavigationOffering } from "@/lib/retiredQaOffering";
import {
  navIndustries,
  navServices,
  filterOfferingsByIndustry,
  getSolutionsForIndustry,
  type NavIndustry,
  type NavServiceGroup,
  type NavSolution,
} from "@/lib/commercialNavMap";

/* ─── i18n strings ─── */
const SIDEBAR_T: Record<SiteLang, {
  industry: string;
  solutions: string;
  services: string;
  overview: string;
  startPrompt: string;
  browsePages: string;
  closeNav: string;
  exploreServices: string;
  closeQuickNav: string;
  browse: string;
}> = {
  en: {
    industry: "Industry",
    solutions: "Solutions",
    services: "Services",
    overview: "Overview →",
    startPrompt: "Start by selecting your industry",
    browsePages: "Browse pages",
    closeNav: "Close navigation",
    exploreServices: "Explore services by industry",
    closeQuickNav: "Close quick navigation",
    browse: "Browse",
  },
  es: {
    industry: "Industria",
    solutions: "Soluciones",
    services: "Servicios",
    overview: "Vista general →",
    startPrompt: "Comienza seleccionando tu industria",
    browsePages: "Explorar páginas",
    closeNav: "Cerrar navegación",
    exploreServices: "Explorar servicios por industria",
    closeQuickNav: "Cerrar navegación rápida",
    browse: "Explorar",
  },
  "zh-cn": {
    industry: "行业",
    solutions: "解决方案",
    services: "服务",
    overview: "概览 →",
    startPrompt: "请先选择您的行业",
    browsePages: "浏览页面",
    closeNav: "关闭导航",
    exploreServices: "按行业浏览服务",
    closeQuickNav: "关闭快速导航",
    browse: "浏览",
  },
};

/* ─── Types ─── */
interface OfferingRow {
  title: string;
  href: string | null;
  industries: string[];
  service_slug: string;
}

/* ─── Stagger animation variants ─── */
const listContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.03 },
  },
};
const listItem = {
  hidden: { opacity: 0, x: -6 },
  show: { opacity: 1, x: 0, transition: { duration: 0.15 } },
};

/* ─── Popover Body ─── */
const PopoverBody = ({
  selectedIndustry,
  setSelectedIndustry,
  serviceGroups,
  solutions,
  currentPath,
  expandedServices,
  toggleService,
  allOfferings,
  t,
}: {
  selectedIndustry: NavIndustry | null;
  setSelectedIndustry: (i: NavIndustry | null) => void;
  serviceGroups: NavServiceGroup[];
  solutions: NavSolution[];
  currentPath: string;
  expandedServices: Set<string>;
  toggleService: (label: string) => void;
  allOfferings: OfferingRow[];
  t: (typeof SIDEBAR_T)[SiteLang];
}) => {
  const isActive = (href: string) => currentPath === href || currentPath === href + "/";

  return (
    <div className="flex flex-col gap-1 font-sans">
      {/* ── Industry filter ── */}
      <div className="pb-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70 mb-2 px-1">
          {t.industry}
        </p>
        <motion.div
          className="flex flex-col gap-px"
          variants={listContainer}
          initial="hidden"
          animate="show"
        >
          {navIndustries.map((ind) => {
            const active = selectedIndustry?.label === ind.label;
            return (
              <motion.button
                key={ind.label}
                variants={listItem}
                onClick={() => setSelectedIndustry(active ? null : ind)}
                className={`group flex items-center gap-2.5 px-2.5 py-[7px] rounded-lg text-left text-[13px] leading-tight transition-all duration-150 ${
                  active
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-foreground/75 hover:bg-muted/80 hover:text-foreground"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full shrink-0 transition-transform duration-150 ${
                    active ? "scale-125" : "group-hover:scale-110"
                  }`}
                  style={{ backgroundColor: ind.color }}
                />
                <span className="truncate">{ind.label}</span>
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      {/* ── Solutions ── */}
      <AnimatePresence mode="wait">
        {selectedIndustry && solutions.length > 0 && (
          <motion.div
            key={`solutions-${selectedIndustry.label}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/40 pt-3 pb-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70 mb-2 px-1">
                {t.solutions}
              </p>
              <motion.div
                className="flex flex-col gap-px"
                variants={listContainer}
                initial="hidden"
                animate="show"
              >
                {solutions.map((sol) => (
                  <motion.div key={sol.href} variants={listItem}>
                    <Link
                      to={sol.href}
                      className={`block px-2.5 py-[7px] rounded-lg text-[13px] leading-tight transition-all duration-150 ${
                        isActive(sol.href)
                          ? "text-primary font-semibold bg-primary/5"
                          : "text-foreground/75 hover:bg-muted/80 hover:text-foreground"
                      }`}
                    >
                      {sol.label}
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Services / Offerings ── */}
      <AnimatePresence mode="wait">
        {selectedIndustry && serviceGroups.length > 0 && (
          <motion.div
            key={`services-${selectedIndustry.label}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/40 pt-3 pb-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70 mb-2 px-1">
                {t.services}
              </p>
              <motion.div
                className="flex flex-col gap-px"
                variants={listContainer}
                initial="hidden"
                animate="show"
              >
                {serviceGroups.map((group) => {
                  const expanded = expandedServices.has(group.label);
                  return (
                    <motion.div key={group.label} variants={listItem}>
                      <button
                        onClick={() => toggleService(group.label)}
                        className={`flex items-center gap-2 w-full px-2.5 py-[7px] rounded-lg text-left text-[13px] leading-tight transition-all duration-150 hover:bg-muted/80 ${
                          isActive(group.href) ? "text-primary font-semibold" : "text-foreground/75 hover:text-foreground"
                        }`}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: group.color }}
                        />
                        <span className="flex-1 truncate">{group.label}</span>
                        <motion.span
                          animate={{ rotate: expanded ? 90 : 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
                        </motion.span>
                      </button>

                      <AnimatePresence>
                        {expanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.15 }}
                            className="overflow-hidden"
                          >
                            <div className="ml-[18px] border-l-2 border-border/30 pl-3 py-0.5 my-0.5">
                              <Link
                                to={group.href}
                                className={`block py-[5px] text-[12px] transition-colors duration-150 ${
                                  isActive(group.href)
                                    ? "text-primary font-semibold"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {t.overview}
                              </Link>
                              {group.offerings.map((o) => (
                                <Link
                                  key={o.href}
                                  to={o.href}
                                  className={`block py-[5px] text-[12px] transition-colors duration-150 ${
                                    isActive(o.href)
                                      ? "text-primary font-semibold"
                                      : "text-muted-foreground hover:text-foreground"
                                  }`}
                                >
                                  {o.title}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Prompt to select industry ── */}
      {!selectedIndustry && (
        <motion.div
          className="border-t border-border/40 pt-4 pb-1"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.4, ease: "easeOut" }}
        >
          <div className="flex items-start gap-2.5 px-1">
            <motion.div
              className="mt-0.5 shrink-0"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.45, type: "spring", stiffness: 400, damping: 15 }}
            >
              <ChevronUp className="w-4 h-4 text-primary" />
            </motion.div>
            <div>
              <motion.p
                className="text-[12.5px] font-medium text-foreground/90 leading-snug"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35, duration: 0.3 }}
              >
                {t.startPrompt}
              </motion.p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

/* ─── Main FAB + Popover Component ─── */
const CommercialBrowseSidebar = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const lang = useLanguage();
  const t = SIDEBAR_T[lang];

  const [open, setOpen] = useState(false);
  
  const [selectedIndustry, setSelectedIndustryRaw] = useState<NavIndustry | null>(() => {
    try {
      const stored = sessionStorage.getItem("browse-industry");
      if (stored) {
        const parsed = JSON.parse(stored);
        return navIndustries.find((i) => i.label === parsed.label) ?? null;
      }
    } catch {}
    return null;
  });
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());

  const setSelectedIndustry = useCallback((ind: NavIndustry | null) => {
    setSelectedIndustryRaw(ind);
    try {
      if (ind) {
        sessionStorage.setItem("browse-industry", JSON.stringify({ label: ind.label }));
      } else {
        sessionStorage.removeItem("browse-industry");
      }
    } catch {}
  }, []);

  const toggleService = useCallback((label: string) => {
    setExpandedServices((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  }, []);

  const { data: storedOfferings = [] } = useQuery({
    queryKey: ["browse-sidebar-offerings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_offerings")
        .select("title, href, industries, service_slug")
        .order("sort_order");
      if (error) throw error;
      return data as OfferingRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const allOfferings = useMemo(() => [
    ...storedOfferings.filter((o) => !isRemovedQaNavigationOffering(o.href) && !(lang === "en" && o.service_slug === "quality-assurance")),
    ...(lang === "en" ? QA_OVERVIEW_CARDS.map((o) => ({ title: o.title, href: o.href, industries: o.industries, service_slug: "quality-assurance" })) : []),
  ], [storedOfferings, lang]);

  const serviceGroups = useMemo(
    () => (selectedIndustry ? filterOfferingsByIndustry(allOfferings, selectedIndustry.label) : []),
    [selectedIndustry, allOfferings]
  );

  const solutions = useMemo(
    () => (selectedIndustry ? getSolutionsForIndustry(selectedIndustry.label) : []),
    [selectedIndustry]
  );

  const bodyProps = {
    selectedIndustry,
    setSelectedIndustry,
    serviceGroups,
    solutions,
    currentPath,
    expandedServices,
    toggleService,
    allOfferings,
    t,
  };

  return (
    <>
      {/* FAB trigger — bottom-right, above scroll-to-top buttons */}
      <div id="commercial-browse-fab" className="fixed bottom-[4.75rem] right-6 z-50 group">
        <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap bg-foreground/90 text-background text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg">
          {open ? t.closeNav : t.exploreServices}
        </div>
        <motion.button
          onClick={() => setOpen((v) => !v)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`relative flex items-center justify-center w-11 h-11 rounded-full transition-colors duration-200 overflow-hidden ${
            open
              ? "bg-foreground/10 text-foreground shadow-md backdrop-blur-sm"
              : "text-white shadow-lg"
          }`}
          aria-label={open ? t.closeQuickNav : t.browsePages}
        >
          {!open && (
            <span className="absolute inset-0 bg-qbd-dark" aria-hidden="true" />
          )}
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span
                key="close"
                className="relative z-10"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <X className="w-5 h-5" />
              </motion.span>
            ) : (
              <motion.span
                key="compass"
                className="relative z-10"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <img src={browseFabIcon} alt={t.browse} className="w-11 h-11 object-cover" />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* Popover panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Click-away overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-40 bg-background/20 backdrop-blur-[2px]"
              onClick={() => setOpen(false)}
            />

            {/* Floating panel — anchored to bottom-right, above FAB */}
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ type: "spring", damping: 28, stiffness: 380 }}
              className="fixed bottom-[8rem] right-6 z-50 w-[calc(100vw-3rem)] sm:w-[290px] max-h-[calc(100vh-12rem)] sm:max-h-[70vh] overflow-y-auto overscroll-contain rounded-2xl border border-border/50 bg-card/95 backdrop-blur-md shadow-2xl shadow-primary/5"
            >
              <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-md border-b border-border/30 px-4 py-3 rounded-t-2xl">
                <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground/90">
                  {t.browsePages}
                </h2>
              </div>
              <div className="px-4 py-3">
                <PopoverBody {...bodyProps} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CommercialBrowseSidebar;
